import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { prepareHeaders } from '../configuration/tokenHandling/prepareHeaders';

export type Address = { name: string; street: string; city: string; postal_code: string; country_code: string };
export type Location = { id?: string; address: Address };
export type RequestedDate = { date: string; time?: string };
export type PickupDeviation =
    | 'none'
    | 'date_changed'
    | 'time_changed'
    | 'request_missing'
    | 'confirmation_missing'
    | 'not_comparable';
export type RequestStatus = 'DRAFT' | 'SCHEDULED' | 'SENT' | 'MISSED';
export type TransportRequest = {
    id: string;
    status: RequestStatus;
    sent_at?: string;
    requested_pickup: RequestedDate;
    requested_delivery?: RequestedDate;
    ship_from: Location;
    ship_to: Location;
    unloading_point_id?: string;
    load_type: string;
    supplier_id: string;
    supplier_name?: string;
    source_goods_ids: string[];
    referenced_requested_amounts?: { article_number: string; quantity: number; unit: string; article_name?: string }[];
    original_required_delivery?: RequestedDate;
    storage_place_id?: string;
    material_details_complete?: boolean;
    materials: { article_number: string; quantity: number; unit: string; article_name?: string }[];
    total_volume_m3?: number;
    total_gross_weight_kg?: number;
    handling_unit_count: number;
};
export type Transport = {
    id: string;
    transport_number?: string;
    requested_pickup_date: string;
    requested_pickup_time?: string;
    estimated_pickup_start?: string;
    estimated_pickup_end?: string;
    carrier?: { carrier_name: string; truck_license_plate_number?: string };
    handling_unit_groups: {
        transport_request_id: string;
        locations: { ship_from: Location; ship_to: Location; place_of_discharge: string; storage_place?: string };
        handling_units: {
            id: string;
            loads: {
                type: string;
                article_number?: string;
                quantity?: string;
                unit?: string;
                sequence_flow_name?: string;
            }[];
        }[];
        matches: boolean;
        request?: Pick<
            TransportRequest,
            'status' | 'sent_at' | 'requested_pickup' | 'requested_delivery' | 'load_type'
        >;
        pickup_deviation: PickupDeviation;
        pickup_time_zone: string;
    }[];
};
export type OverviewSort =
    | 'order_number'
    | 'ordered_at'
    | 'pickup'
    | 'delivery'
    | 'ship_from'
    | 'ship_to'
    | 'carrier'
    | 'plate'
    | 'load_type'
    | 'materials'
    | 'volume'
    | 'pickup_deviation';
export type OverviewQuery = {
    search: string;
    plantIds: string[];
    carriers: string[];
    deliveryFrom?: string;
    deliveryTo?: string;
    sort: OverviewSort;
    direction: 'asc' | 'desc';
    offset: number;
    limit: number;
};
export type ResourcePage<T> = {
    items: T[];
    total: number;
    offset: number;
    limit: number;
    unfiltered_total: number;
    facets: { plants: { id: string; label: string }[]; carriers: { id: string; label: string }[] };
};
export const paramsFor = (query: OverviewQuery) => {
    const params = new URLSearchParams({
        search: query.search,
        sort: query.sort,
        direction: query.direction,
        offset: String(query.offset),
        limit: String(query.limit),
    });
    query.plantIds.forEach(id => {
        params.append('plant_id', id);
    });
    query.carriers.forEach(carrier => {
        params.append('carrier', carrier);
    });
    if (query.deliveryFrom) {
        params.set('delivery_from', query.deliveryFrom);
    }
    if (query.deliveryTo) {
        params.set('delivery_to', query.deliveryTo);
    }
    return params;
};

export const controlTowerApi = createApi({
    reducerPath: 'controlTowerApi',
    tagTypes: ['TransportData'],
    baseQuery: fetchBaseQuery({
        baseUrl: import.meta.env.VITE_SCI_API_BASE_URL || window.location.origin,
        prepareHeaders: headers => {
            prepareHeaders(headers);
            if (import.meta.env.DEV && import.meta.env.VITE_SCI_LOCAL_TOKEN) {
                headers.set('authorization', `Bearer ${import.meta.env.VITE_SCI_LOCAL_TOKEN}`);
            }
            return headers;
        },
    }),
    endpoints: builder => ({
        requests: builder.query<ResourcePage<TransportRequest>, OverviewQuery>({
            providesTags: ['TransportData'],
            query: query => `/control-tower/transport-requests?${paramsFor(query)}`,
            serializeQueryArgs: ({ endpointName, queryArgs }) =>
                JSON.stringify([endpointName, { ...queryArgs, offset: 0 }]),
            merge: (cache, incoming, { arg }) => {
                if (arg.offset === 0) {
                    return incoming;
                }
                const items = new Map(cache.items.map(item => [item.id, item]));
                incoming.items.forEach(item => {
                    items.set(item.id, item);
                });
                return { ...incoming, items: [...items.values()] };
            },
            forceRefetch: ({ currentArg, previousArg }) => currentArg?.offset !== previousArg?.offset,
        }),
        transports: builder.query<ResourcePage<Transport>, OverviewQuery>({
            providesTags: ['TransportData'],
            query: query => `/control-tower/transports?${paramsFor(query)}`,
            serializeQueryArgs: ({ endpointName, queryArgs }) =>
                JSON.stringify([endpointName, { ...queryArgs, offset: 0 }]),
            merge: (cache, incoming, { arg }) => {
                if (arg.offset === 0) {
                    return incoming;
                }
                const items = new Map(cache.items.map(item => [item.id, item]));
                incoming.items.forEach(item => {
                    items.set(item.id, item);
                });
                return { ...incoming, items: [...items.values()] };
            },
            forceRefetch: ({ currentArg, previousArg }) => currentArg?.offset !== previousArg?.offset,
        }),
    }),
});
export const { useRequestsQuery, useTransportsQuery } = controlTowerApi;
